/**
 * PMS Role Permissions Utility
 * Implements role inheritance: Employee < Manager < HR_Admin < Super_Admin
 * Each higher role has all PMS capabilities of lower roles plus their own.
 * HR_Admin can access PMS and EMS expense visibility, but not Award Nominations.
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 19.2, 19.3, 19.4, 19.5
 */

import { PMSRole } from '../types/pms';
import { UserRole } from '../types/user';

/**
 * Role hierarchy levels (higher number = more permissions)
 * Requirements: 1.1
 */
export const PMS_ROLE_HIERARCHY: Record<PMSRole, number> = {
  [PMSRole.EMPLOYEE]: 1,
  [PMSRole.MANAGER]: 2,
  [PMSRole.HR_ADMIN]: 3,
  [PMSRole.SUPER_ADMIN]: 4,
};

/**
 * Permission types for the Performance Management System
 */
export enum PMSPermission {
  // Employee permissions
  VIEW_OWN_OBJECTIVES = 'VIEW_OWN_OBJECTIVES',
  CREATE_OBJECTIVE = 'CREATE_OBJECTIVE',
  UPDATE_PROGRESS = 'UPDATE_PROGRESS',
  ADD_EMPLOYEE_COMMENTS = 'ADD_EMPLOYEE_COMMENTS',
  VIEW_OWN_REVIEWS = 'VIEW_OWN_REVIEWS',
  PARTICIPATE_CHAT = 'PARTICIPATE_CHAT',
  UPLOAD_ATTACHMENT = 'UPLOAD_ATTACHMENT',
  CREATE_MILESTONE = 'CREATE_MILESTONE',

  // Manager permissions (includes Employee)
  APPROVE_OBJECTIVE = 'APPROVE_OBJECTIVE',
  RETURN_OBJECTIVE = 'RETURN_OBJECTIVE',
  AMEND_OBJECTIVE = 'AMEND_OBJECTIVE',
  ADD_MANAGER_COMMENTS = 'ADD_MANAGER_COMMENTS',
  CONDUCT_REVIEW = 'CONDUCT_REVIEW',
  ASSIGN_RATING = 'ASSIGN_RATING',
  VIEW_TEAM_OBJECTIVES = 'VIEW_TEAM_OBJECTIVES',
  VIEW_TEAM_REVIEWS = 'VIEW_TEAM_REVIEWS',

  // HR Admin permissions (includes Manager)
  CONFIGURE_REVIEW_CYCLE = 'CONFIGURE_REVIEW_CYCLE',
  LOCK_OBJECTIVES = 'LOCK_OBJECTIVES',
  UNLOCK_OBJECTIVES = 'UNLOCK_OBJECTIVES',
  EXPORT_REPORTS = 'EXPORT_REPORTS',
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',
  VIEW_ALL_OBJECTIVES = 'VIEW_ALL_OBJECTIVES',

  // Super Admin permissions (includes HR Admin)
  MANAGE_ALL_PMS = 'MANAGE_ALL_PMS',
}

/**
 * Permissions granted at each role level (not cumulative — inheritance is computed).
 */
const ROLE_PERMISSIONS: Record<PMSRole, PMSPermission[]> = {
  [PMSRole.EMPLOYEE]: [
    PMSPermission.VIEW_OWN_OBJECTIVES,
    PMSPermission.CREATE_OBJECTIVE,
    PMSPermission.UPDATE_PROGRESS,
    PMSPermission.ADD_EMPLOYEE_COMMENTS,
    PMSPermission.VIEW_OWN_REVIEWS,
    PMSPermission.PARTICIPATE_CHAT,
    PMSPermission.UPLOAD_ATTACHMENT,
    PMSPermission.CREATE_MILESTONE,
  ],
  [PMSRole.MANAGER]: [
    PMSPermission.APPROVE_OBJECTIVE,
    PMSPermission.RETURN_OBJECTIVE,
    PMSPermission.AMEND_OBJECTIVE,
    PMSPermission.ADD_MANAGER_COMMENTS,
    PMSPermission.CONDUCT_REVIEW,
    PMSPermission.ASSIGN_RATING,
    PMSPermission.VIEW_TEAM_OBJECTIVES,
    PMSPermission.VIEW_TEAM_REVIEWS,
  ],
  [PMSRole.HR_ADMIN]: [
    PMSPermission.CONFIGURE_REVIEW_CYCLE,
    PMSPermission.LOCK_OBJECTIVES,
    PMSPermission.UNLOCK_OBJECTIVES,
    PMSPermission.EXPORT_REPORTS,
    PMSPermission.VIEW_ANALYTICS,
    PMSPermission.VIEW_ALL_OBJECTIVES,
  ],
  [PMSRole.SUPER_ADMIN]: [
    PMSPermission.MANAGE_ALL_PMS,
  ],
};

/**
 * Get the hierarchy level of a PMS role.
 */
export const getPMSRoleLevel = (role: PMSRole): number => {
  return PMS_ROLE_HIERARCHY[role] || 0;
};

/**
 * Check if role1 is higher or equal to role2 in the hierarchy.
 */
export const isPMSRoleHigherOrEqual = (
  role1: PMSRole,
  role2: PMSRole
): boolean => {
  return getPMSRoleLevel(role1) >= getPMSRoleLevel(role2);
};

/**
 * Get all permissions for a PMS role (including inherited from lower roles).
 * Requirements: 1.2
 */
export const getPMSPermissions = (role: PMSRole): PMSPermission[] => {
  const permissions: PMSPermission[] = [];
  const roleLevel = getPMSRoleLevel(role);

  Object.entries(PMS_ROLE_HIERARCHY).forEach(([r, level]) => {
    if (level <= roleLevel) {
      permissions.push(...ROLE_PERMISSIONS[r as PMSRole]);
    }
  });

  return [...new Set(permissions)];
};

/**
 * Check if a PMS role has a specific permission.
 */
export const hasPMSPermission = (
  role: PMSRole,
  permission: PMSPermission
): boolean => {
  return getPMSPermissions(role).includes(permission);
};

/**
 * Map an EMS UserRole to the corresponding PMSRole.
 * HR_ADMIN → HR_ADMIN (PMS only), FINANCE_ADMIN → EMPLOYEE (no special PMS access).
 * Requirements: 1.1, 1.7
 */
export const mapUserRoleToPMSRole = (role: UserRole): PMSRole => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return PMSRole.SUPER_ADMIN;
    case UserRole.HR_ADMIN:
      return PMSRole.HR_ADMIN;
    case UserRole.MANAGER:
      return PMSRole.MANAGER;
    case UserRole.EMPLOYEE:
    case UserRole.FINANCE_ADMIN:
    default:
      return PMSRole.EMPLOYEE;
  }
};

// --- Guard Functions ---

/**
 * Check if the role can create objectives (Employee+).
 * Requirements: 1.2
 */
export const canCreateObjective = (role: PMSRole): boolean => {
  return isPMSRoleHigherOrEqual(role, PMSRole.EMPLOYEE);
};

/**
 * Check if the role can approve objectives (Manager+).
 * Requirements: 1.3
 */
export const canApproveObjective = (role: PMSRole): boolean => {
  return isPMSRoleHigherOrEqual(role, PMSRole.MANAGER);
};

/**
 * Check if the role can conduct reviews (Manager+).
 * Requirements: 1.3
 */
export const canConductReview = (role: PMSRole): boolean => {
  return isPMSRoleHigherOrEqual(role, PMSRole.MANAGER);
};

/**
 * Check if the role can configure review cycles (HR_Admin+).
 * Requirements: 1.4
 */
export const canConfigureReviewCycle = (role: PMSRole): boolean => {
  return isPMSRoleHigherOrEqual(role, PMSRole.HR_ADMIN);
};

/**
 * Check if the role can lock objectives (HR_Admin+).
 * Requirements: 1.4
 */
export const canLockObjectives = (role: PMSRole): boolean => {
  return isPMSRoleHigherOrEqual(role, PMSRole.HR_ADMIN);
};

/**
 * Check if the role can export reports (HR_Admin+).
 * Requirements: 1.4
 */
export const canExportReports = (role: PMSRole): boolean => {
  return isPMSRoleHigherOrEqual(role, PMSRole.HR_ADMIN);
};

// --- Sidebar Menu ---

export interface PMSMenuItem {
  path: string;
  label: string;
}

/**
 * Get the sidebar menu items permitted for a given PMS role.
 * Requirements: 19.2, 19.3, 19.4, 19.5
 */
export const getPMSMenuItems = (role: PMSRole): PMSMenuItem[] => {
  const items: PMSMenuItem[] = [];

  // Employee+: My Objectives, My Reviews
  items.push({ path: '/performance/objectives', label: 'My Objectives' });
  items.push({ path: '/performance/reviews', label: 'My Reviews' });

  // Manager+: Team Objectives, Pending Approvals, Team Reviews
  if (isPMSRoleHigherOrEqual(role, PMSRole.MANAGER)) {
    items.push({ path: '/performance/team', label: 'Team Objectives' });
    items.push({ path: '/performance/approvals', label: 'Pending Approvals' });
    items.push({ path: '/performance/team-reviews', label: 'Team Reviews' });
  }

  // HR_Admin+: Analytics Dashboard, Review Cycle Configuration, Reports, Objective Lock Management
  if (isPMSRoleHigherOrEqual(role, PMSRole.HR_ADMIN)) {
    items.push({ path: '/performance/annual-cycle', label: 'Annual Objective Cycle' });
    items.push({ path: '/performance/analytics', label: 'Analytics Dashboard' });
    items.push({ path: '/performance/review-cycles', label: 'Review Cycle Configuration' });
    items.push({ path: '/performance/reports', label: 'Reports' });
    items.push({ path: '/performance/lock-management', label: 'Objective Lock Management' });
  }

  return items;
};

/**
 * Modules that HR_Admin is restricted FROM accessing.
 * HR_Admin can access PMS and EMS expense visibility, but not Award Nominations.
 * Requirements: 1.5, 1.7
 */
export const HR_ADMIN_RESTRICTED_MODULES = ['AWARD_NOMINATION'] as const;

/**
 * Check if a UserRole has access to a given module.
 * HR_Admin is restricted from Award Nominations, but can access EMS expense visibility.
 * Requirements: 1.5, 1.7
 */
export const hasModuleAccess = (
  role: UserRole,
  module: 'EMS' | 'AWARD_NOMINATION' | 'PMS'
): boolean => {
  if (role === UserRole.HR_ADMIN) {
    return module === 'PMS' || module === 'EMS';
  }
  return true;
};
